/**
 * Bundle Add to Cart Button Validator
 * MOQ (Minimum Order Quantity) = 6 units
 * Button is enabled when cart quantity + bundle quantity >= 6 units
 */

class BundleATCValidator {
  constructor() {
    this.MOQ = 6; // Minimum Order Quantity
    this.observer = null;
    this.debounceTimer = null;
    this.lastTotalQuantity = -1;
    this.cartQuantity = 0;
    this.parentProductId = null;
    this.init();
  }

  init() {
    console.log('BundleATCValidator: Initializing with MOQ =', this.MOQ);
    this.getParentProductId();
    this.fetchCartQuantity().then(() => {
      this.setupMutationObserver();
      this.validate();
    });
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  getParentProductId() {
    const bundleContainer = document.querySelector('.gbbMixPageWrapper');
    if (bundleContainer) {
      this.parentProductId = bundleContainer.getAttribute('parent-product-id');
    }
  }

  async fetchCartQuantity() {
    try {
      const response = await fetch('/cart.json');
      const cart = await response.json();
      
      // Count quantity of items matching the parent product ID
      this.cartQuantity = 0;
      
      if (this.parentProductId && cart.items) {
        cart.items.forEach(item => {
          if (item.product_id && item.product_id.toString() === this.parentProductId) {
            this.cartQuantity += item.quantity;
          }
        });
      }
      
      console.log('BundleATCValidator: Cart quantity:', this.cartQuantity);
    } catch (error) {
      console.warn('BundleATCValidator: Failed to fetch cart:', error);
      this.cartQuantity = 0;
    }
  }

  setupMutationObserver() {
    const bundleContainer = document.querySelector('.gbbMixPageWrapper');
    if (!bundleContainer) {
      return;
    }

    this.observer = new MutationObserver(() => {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      this.debounceTimer = setTimeout(() => {
        this.validate();
      }, 100);
    });

    this.observer.observe(bundleContainer, {
      attributes: true,
      attributeFilter: ['data-product-quantity'],
      subtree: true
    });
  }

  getBundleQuantity() {
    const productWrappers = document.querySelectorAll('.gbbMixCascadeProductWrapper');
    let total = 0;
    
    productWrappers.forEach(wrapper => {
      const qty = parseInt(wrapper.getAttribute('data-product-quantity')) || 0;
      total += qty;
    });

    return total;
  }

  validate() {
    const atcButton = document.querySelector('.gbbMixCascadeAddToCartBtn');
    if (!atcButton) {
      return;
    }

    const bundleQuantity = this.getBundleQuantity();
    const totalQuantity = this.cartQuantity + bundleQuantity;
    
    // Only update if total quantity actually changed
    if (totalQuantity === this.lastTotalQuantity) {
      return;
    }
    
    this.lastTotalQuantity = totalQuantity;
    const meetsMOQ = totalQuantity >= this.MOQ;

    console.log('BundleATCValidator: Cart:', this.cartQuantity, '| Bundle:', bundleQuantity, '| Total:', totalQuantity, '| MOQ:', this.MOQ, '| Enabled:', meetsMOQ);

    if (meetsMOQ) {
      this.enableButton(atcButton);
    } else {
      this.disableButton(atcButton, totalQuantity);
    }
  }

  disableButton(button, totalQuantity) {
    button.classList.add('gbbMixCascadeAddToCartBtn--disabled');
    button.classList.add('gbbMixCascadeAddToCartBtn--disabled-conditionsNotMet');
    button.style.pointerEvents = 'none';
    button.style.opacity = '0.6';
    
    const label = button.querySelector('.gbbMixCascadeAddToCartBtnLabel');
    if (label) {
      const unitsNeeded = this.MOQ - totalQuantity;
      if (this.cartQuantity > 0) {
        label.textContent = `Add ${unitsNeeded} more unit${unitsNeeded !== 1 ? 's' : ''} (${this.cartQuantity} in cart)`;
      } else {
        label.textContent = `Add ${unitsNeeded} more unit${unitsNeeded !== 1 ? 's' : ''} to meet MOQ (${this.MOQ})`;
      }
    }
  }

  enableButton(button) {
    button.classList.remove('gbbMixCascadeAddToCartBtn--disabled');
    button.classList.remove('gbbMixCascadeAddToCartBtn--disabled-conditionsNotMet');
    button.style.pointerEvents = '';
    button.style.opacity = '';
    
    const label = button.querySelector('.gbbMixCascadeAddToCartBtnLabel');
    if (label) {
      label.textContent = 'Add Bundle to Cart';
    }
  }

  async refreshCartQuantity() {
    await this.fetchCartQuantity();
    this.validate();
  }
}

// Global instance
window.BundleATCValidatorInstance = null;

function initializeBundleATCValidator() {
  // Destroy existing instance
  if (window.BundleATCValidatorInstance) {
    window.BundleATCValidatorInstance.destroy();
  }

  // Only initialize if bundle widget exists
  const bundleContainer = document.querySelector('.gbbMixPageWrapper');
  if (bundleContainer) {
    window.BundleATCValidatorInstance = new BundleATCValidator();
  }
}

// Wait for bundle widget to be ready before initializing
document.addEventListener('gbbMix-widget-ready', () => {
  console.log('BundleATCValidator: Bundle widget ready, initializing...');
  setTimeout(initializeBundleATCValidator, 300);
});

// Listen for data loaded event
document.addEventListener('gbbMix-next-data-loaded', () => {
  if (window.BundleATCValidatorInstance) {
    window.BundleATCValidatorInstance.validate();
  }
});

// Listen for cart updates (when items are added to cart)
document.addEventListener('cart:updated', () => {
  if (window.BundleATCValidatorInstance) {
    console.log('BundleATCValidator: Cart updated, refreshing...');
    window.BundleATCValidatorInstance.refreshCartQuantity();
  }
});

// Listen for cart drawer changes
document.addEventListener('cart:change', () => {
  if (window.BundleATCValidatorInstance) {
    console.log('BundleATCValidator: Cart changed, refreshing...');
    window.BundleATCValidatorInstance.refreshCartQuantity();
  }
});

// Listen for cart refresh events
document.addEventListener('cart:refresh', () => {
  if (window.BundleATCValidatorInstance) {
    window.BundleATCValidatorInstance.refreshCartQuantity();
  }
});

// Also listen for Shopify theme cart updates
document.addEventListener('product:variant-change', () => {
  if (window.BundleATCValidatorInstance) {
    window.BundleATCValidatorInstance.refreshCartQuantity();
  }
});

// Watch for cart drawer item count changes
const observeCartDrawer = () => {
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    const observer = new MutationObserver(() => {
      if (window.BundleATCValidatorInstance) {
        window.BundleATCValidatorInstance.refreshCartQuantity();
      }
    });
    
    observer.observe(cartDrawer, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-count']
    });
  }
};

// Initialize cart drawer observer when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', observeCartDrawer);
} else {
  observeCartDrawer();
}
